import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useSocket } from 'socket.io-react-hook';
import { _getToken, notificationsAxiosApi } from 'src/Config/axiosApi';
import {
  NotificationEvent,
  TRIAGE_READ_EVENT,
  TriageReadFrame,
  triageBucketOf,
} from '../types/notifications.types';

/**
 * The compact triage map (`GET /notifications/triage/index`, spec §4.5) that
 * the opener group table and the Descalces page consume to mark a row that has
 * unread `counter_stuck` / `orphaned_automation_fill` events.
 *
 * 🚨 The consumer keys "unreadable" on `data === undefined`, NEVER on
 * `isError`: react-query's `isError` is sticky and retains `data`, and this
 * repo has already lost a whole outage to reading that flag as truth. That is
 * why this hook does not expose `isError` at all — a surface with no chips and
 * no header warning would read as "nothing open" over a map that could not be
 * read.
 */

export interface TriageCounts {
  counter: number;
  orphan: number;
  latestAt: string | null;
  worstSeverity: string | null;
}

export interface TriageIndex {
  byRuleId: Record<string, TriageCounts>;
  byPairId: Record<string, TriageCounts>;
  byGroupId: Record<string, { counter: number; orphan: number; rules: number }>;
  /**
   * Orphan events with no `pairId` — nothing can carry their mark.
   *
   * 🚨 Counted over the UNSCOPED window on purpose: the pair scope removes
   * exactly these cards, so scoping this number too would report `0` over real
   * naked fills. `TriageIndexNotice` renders it beside `hidden`, which is the
   * only surface outside the tray that names them.
   */
  unattributed: number;
  /** Unread cards in the window the pair scope removed from the maps above. */
  hidden?: number;
  /** True when the response includes cards on retired pairs. */
  includesRetired?: boolean;
  /** True when the scope could not be resolved and the maps are UNSCOPED. */
  pairScopeCapped?: boolean;
  windowDays: number;
  /** The key cap was hit: the map is NOT complete and must not present as such. */
  truncated: boolean;
  computedAt: string;
}

export const TRIAGE_INDEX_KEY = ['triage-index'] as const;

export const TRIAGE_INDEX_POLL_MS = 15_000;

/** Window a burst of socket frames is folded into before one refetch. */
export const TRIAGE_REFRESH_COALESCE_MS = 250;

/**
 * A last success older than this is STALE: the chips stay (they were true once)
 * but they are greyed and the header carries the age. Three poll intervals, so
 * one dropped request is not an alarm.
 */
export const TRIAGE_INDEX_STALE_AFTER_MS = TRIAGE_INDEX_POLL_MS * 3;

/** Thrown when a 200 carries a payload the surfaces cannot safely read. */
export class TriageIndexMalformedError extends Error {
  constructor() {
    super('triage index is missing a required field');
    this.name = 'TriageIndexMalformedError';
  }
}

/**
 * Every field here is load-bearing for a DIFFERENT lie:
 *  · the three maps — a consumer indexes into them directly, and a missing one
 *    would throw mid-render or, worse, read as "this rule has nothing";
 *  · `truncated` — absent, a capped map presents as complete;
 *  · `computedAt` — absent, nothing can be aged.
 * A payload missing any of them is rejected so `data` stays `undefined` and the
 * surfaces say *unreadable* instead of *clean*.
 */
export function isUsableTriageIndex(value: unknown): value is TriageIndex {
  const v = value as Partial<TriageIndex> | null | undefined;
  return (
    !!v &&
    !!v.byRuleId &&
    typeof v.byRuleId === 'object' &&
    !!v.byPairId &&
    typeof v.byPairId === 'object' &&
    !!v.byGroupId &&
    typeof v.byGroupId === 'object' &&
    typeof v.unattributed === 'number' &&
    typeof v.truncated === 'boolean' &&
    typeof v.computedAt === 'string'
  );
}

export async function fetchTriageIndex(): Promise<TriageIndex> {
  const { data } = await notificationsAxiosApi.get<TriageIndex>(
    '/notifications/triage/index',
    // Always scoped, and NOT wired to the tray's toggle.
    //
    // The marks this index paints — the opener table cell, the Descalce
    // column, the group badges — live on screens that have no toggle of their
    // own, and the actionable count is the one that belongs there: a badge
    // counting cards on finished pairs sends an operator to a screen that then
    // shows none of them. Following the tray's URL param would also make those
    // badges change because somebody widened a tray on another page, off a
    // query key (`['triage-index']`) that is shared fleet-wide.
    //
    // The consequence is real and accounted for: with the tray widened, the
    // tray shows more than the badges count. The tray says so in its own
    // banner, and `TriageIndexNotice` renders this response's `hidden` beside
    // the marks, so neither side is silent about the difference.
    { params: { retired: 'exclude' } },
  );
  if (!isUsableTriageIndex(data)) throw new TriageIndexMalformedError();
  return data;
}

/* ── The shared socket tap ────────────────────────────────────────────── */

export interface TriageSocketHandlers {
  /** A read/unread write landed somewhere. Frames may arrive DUPLICATED. */
  onRead?: (frame: TriageReadFrame) => void;
  /** A new `counter_stuck` / `orphaned_automation_fill` arrived live. */
  onTriageEvent?: (evt: NotificationEvent) => void;
}

/** How many applied frame entries we remember to recognise a duplicate. */
const READ_FRAME_MEMORY = 5_000;

/**
 * What makes two deliveries of a read frame THE SAME event: the id AND the
 * stamp. Keying on the id alone would swallow the `notification_read` frame
 * that an UNREAD write emits for an id we just cleared — the undo would then
 * reach neither surface until the next poll, and the operator who mis-clicked
 * would be staring at a card that is silently back.
 */
function readFrameEntryKey(id: string, readAt: string | null): string {
  return `${id}@${readAt ?? 'unread'}`;
}

function isReadFrame(value: unknown): value is TriageReadFrame {
  const v = value as Partial<TriageReadFrame> | null | undefined;
  return !!v && Array.isArray(v.ids);
}

/**
 * Listens on the SAME `notifications` connection `useNotificationsSocket`
 * already owns, and delivers exactly-once.
 *
 * ⚠️ The non-function options below must stay byte-identical to
 * `useNotificationsSocket`'s: `socket.io-react-hook` keys its connection cache
 * on a stable hash of them (functions excluded), so a drifted literal here
 * silently opens a SECOND socket to the gateway. The room join
 * (`subscribe_notifications`) is deliberately NOT repeated — that hook owns the
 * handshake, and a second one costs a full stream replay.
 *
 * Exactly-once: a `notification_read` frame whose ids we have all seen is
 * dropped, because the relay runs on several replicas with no consumer group
 * and re-sends the same frame.
 */
export function useTriageSocketFrames(
  handlers: TriageSocketHandlers,
  opts?: { enabled?: boolean },
): void {
  const wsUrl = import.meta.env.VITE_NOTIFICATIONS_WS_URL;
  const accessToken = _getToken();
  const { socket } = useSocket(wsUrl || '', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 30_000,
    auth: (cb: (data: { token: string }) => void) => cb({ token: _getToken() }),
  });

  // Read through a ref so a caller passing inline closures does not
  // resubscribe (and re-arm the dedupe memory) on every render.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const enabled = opts?.enabled !== false;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  /** Applied `id@readAt` entries — see `readFrameEntryKey`. */
  const seenReadIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (typeof socket?.on !== 'function') return;

    const onRead = (frame: unknown) => {
      if (!enabledRef.current || !isReadFrame(frame)) return;
      const readAt = frame.readAt ?? null;
      const fresh = frame.ids.filter(
        (id) => !seenReadIds.current.has(readFrameEntryKey(id, readAt)),
      );
      // Every id already applied with this same stamp ⇒ this is the duplicate
      // delivery, not a second write. Dropping it is what keeps a re-sent
      // frame from re-triggering work on both consumers.
      if (fresh.length === 0) return;
      if (seenReadIds.current.size + fresh.length > READ_FRAME_MEMORY) {
        seenReadIds.current = new Set();
      }
      for (const id of fresh) {
        seenReadIds.current.add(readFrameEntryKey(id, readAt));
      }
      handlersRef.current.onRead?.({ ...frame, readAt, ids: fresh });
    };

    const onNotification = (evt: NotificationEvent) => {
      if (!enabledRef.current) return;
      if (!triageBucketOf(evt?.type)) return;
      handlersRef.current.onTriageEvent?.(evt);
    };

    socket.on(TRIAGE_READ_EVENT, onRead);
    socket.on('notification', onNotification);
    return () => {
      socket.off(TRIAGE_READ_EVENT, onRead);
      socket.off('notification', onNotification);
    };
  }, [socket]);
}

/* ── The index hook ───────────────────────────────────────────────────── */

export interface UseTriageIndexResult {
  /** `undefined` ⇒ the index NEVER loaded ⇒ the consumer shows "unreadable". */
  data: TriageIndex | undefined;
  /** Last success older than 3× the poll interval. */
  isStale: boolean;
  lastSuccessAt: number | null;
}

export function useTriageIndex(opts?: {
  enabled?: boolean;
}): UseTriageIndexResult {
  const enabled = opts?.enabled !== false;

  const query = useQuery<TriageIndex>(TRIAGE_INDEX_KEY, fetchTriageIndex, {
    enabled,
    refetchInterval: TRIAGE_INDEX_POLL_MS,
    refetchOnWindowFocus: true,
    keepPreviousData: true,
    retry: false,
  });

  // A read/unread write anywhere (this tab, another operator, the backend's
  // own auto-clear) changes counts we cannot recompute from the frame: it
  // carries ids, and the index is keyed by rule/pair/group. So the frame is a
  // REFRESH TRIGGER, never a local edit — the server owns the arithmetic. The
  // 15s poll is what makes a dropped frame cost one interval and nothing more.
  const { refetch } = query;
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const pendingRefresh = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (pendingRefresh.current) clearTimeout(pendingRefresh.current);
    },
    [],
  );

  const scheduleRefresh = () => {
    if (!enabled) return;
    // Coalesce a burst (one clear posts many ids and the relay fans them out)
    // into a single read.
    if (pendingRefresh.current) return;
    pendingRefresh.current = setTimeout(() => {
      pendingRefresh.current = null;
      void refetchRef.current();
    }, TRIAGE_REFRESH_COALESCE_MS);
  };

  useTriageSocketFrames(
    { onRead: scheduleRefresh, onTriageEvent: scheduleRefresh },
    { enabled },
  );

  const lastSuccessAt = query.dataUpdatedAt > 0 ? query.dataUpdatedAt : null;

  // Staleness has to be able to flip with NO new data — that is the whole
  // point of it — so the component re-renders on its own cadence. Gated on
  // `enabled` alongside the fetch: a disabled query keeps the cached entry's
  // `dataUpdatedAt`, and nothing is reading the age of a surface that is not
  // on screen.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!enabled || lastSuccessAt === null) return;
    const timer = setInterval(() => tick((n) => n + 1), TRIAGE_INDEX_POLL_MS);
    return () => clearInterval(timer);
  }, [enabled, lastSuccessAt]);

  const isStale =
    lastSuccessAt !== null &&
    Date.now() - lastSuccessAt > TRIAGE_INDEX_STALE_AFTER_MS;

  return { data: query.data, isStale, lastSuccessAt };
}
